import { Settings as SettingsIcon, Building2, Users, Puzzle, Key, Sparkles, Headset } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { GoogleDriveConnection } from '@/components/integrations/google-drive-connection'
import { NotionConnectionCard } from '@/components/integrations/notion-connection'
import { SlackConnectionCard } from '@/components/integrations/slack-connection'
import { ApiKeysTab } from '@/components/settings/api-keys-tab'
import { AtendimentoDaOrganizacao } from '@/components/settings/atendimento-da-organizacao'
import { LlmKeysTab } from '@/components/settings/llm-keys-tab'
import { useOrganizationStore } from '@/stores/organization-store'

const ABAS = ['integrations', 'atendimento', 'organization', 'members', 'api-keys', 'llm-keys']

export function SettingsPage() {
  const { t } = useTranslation()
  const { currentOrganization } = useOrganizationStore()
  // A ABA VEM DA URL (spec 157, D-07): o link do campo travado do agente abre
  // `/settings?tab=atendimento#orcamento`, e a página rola até a seção.
  const [params, setParams] = useSearchParams()
  const abaDaUrl = params.get('tab')
  const aba = abaDaUrl && ABAS.includes(abaDaUrl) ? abaDaUrl : 'integrations'
  const trocarAba = (valor: string) => {
    const proximos = new URLSearchParams(params)
    proximos.set('tab', valor)
    setParams(proximos, { replace: true })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-indigo-400" />
          {t('settings.title')}
        </h1>
        <p className="text-gray-400 mt-2">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Settings Tabs */}
      <Tabs value={aba} onValueChange={trocarAba} className="w-full">
        <TabsList className="flex-wrap">
          <TabsTrigger value="integrations" className="gap-2">
            <Puzzle className="w-4 h-4" />
            {t('settings.tabs.integrations')}
          </TabsTrigger>
          <TabsTrigger value="atendimento" className="gap-2">
            <Headset className="w-4 h-4" />
            Atendimento
          </TabsTrigger>
          <TabsTrigger value="organization" className="gap-2">
            <Building2 className="w-4 h-4" />
            {t('settings.tabs.organization')}
          </TabsTrigger>
          <TabsTrigger value="members" className="gap-2">
            <Users className="w-4 h-4" />
            {t('settings.tabs.members')}
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="gap-2">
            <Key className="w-4 h-4" />
            {t('settings.tabs.apiKeys')}
          </TabsTrigger>
          <TabsTrigger value="llm-keys" className="gap-2">
            <Sparkles className="w-4 h-4" />
            {t('settings.tabs.llmKeys')}
          </TabsTrigger>
        </TabsList>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="space-y-6">
            {/* Google Drive Integration */}
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.01 1.485c-.34-.196-.77-.196-1.11 0L2.25 7.257a1.11 1.11 0 0 0-.557.964v11.558c0 .398.212.765.557.965l8.649 5.771c.34.196.77.196 1.11 0l8.65-5.771a1.11 1.11 0 0 0 .556-.965V8.221c0-.398-.212-.765-.557-.964L12.01 1.485z"/>
                    </svg>
                  </div>
                  {t('settings.integrations.googleDrive.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.integrations.googleDrive.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!currentOrganization ? (
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4">
                      <p className="text-sm text-amber-400">
                        {t('settings.selectOrgWarning')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-200 mb-2">{t('settings.integrations.googleDrive.connectionStatus')}</h4>
                          <GoogleDriveConnection />
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-700/50">
                        <h4 className="font-medium text-gray-200 mb-3">{t('settings.integrations.googleDrive.features')}</h4>
                        <ul className="space-y-2 text-sm text-gray-400">
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.googleDrive.feature1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.googleDrive.feature2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.googleDrive.feature3')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-gray-600 mt-1">✓</span>
                            <span className="text-gray-600">{t('settings.integrations.googleDrive.feature4')}</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notion Integration */}
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.373.466l1.822 1.447zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.84-.046.933-.56.933-1.167V6.354c0-.606-.233-.933-.746-.886l-15.177.887c-.56.046-.747.326-.747.933zm14.337.746c.093.42 0 .84-.42.887l-.7.14v10.264c-.607.327-1.167.514-1.634.514-.746 0-.933-.234-1.493-.934l-4.577-7.186v6.952l1.446.327s0 .84-1.167.84l-3.22.187c-.093-.187 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.213-.14c-.094-.513.28-.886.746-.933l3.221-.186zM2.17 1.4L15.984.14c1.682-.14 2.102.094 2.802.607l3.876 2.707c.467.326.607.7.607 1.167v16.756c0 1.027-.373 1.634-1.68 1.727l-15.458.933c-.98.047-1.447-.093-1.96-.7l-3.127-4.057c-.56-.746-.793-1.306-.793-1.96V2.942c0-.839.374-1.54 1.727-1.633l.193.093z" />
                    </svg>
                  </div>
                  {t('settings.integrations.notion.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.integrations.notion.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!currentOrganization ? (
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4">
                      <p className="text-sm text-amber-400">
                        {t('settings.selectOrgWarning')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-200 mb-2">{t('settings.integrations.notion.connectionStatus')}</h4>
                          <NotionConnectionCard />
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-700/50">
                        <h4 className="font-medium text-gray-200 mb-3">{t('settings.integrations.notion.features')}</h4>
                        <ul className="space-y-2 text-sm text-gray-400">
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.notion.feature1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.notion.feature2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.notion.feature3')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.notion.feature4')}</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Slack Integration */}
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                    </svg>
                  </div>
                  {t('settings.integrations.slack.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.integrations.slack.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!currentOrganization ? (
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4">
                      <p className="text-sm text-amber-400">
                        {t('settings.selectOrgWarning')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-200 mb-2">{t('settings.integrations.slack.connectionStatus')}</h4>
                          <SlackConnectionCard />
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-700/50">
                        <h4 className="font-medium text-gray-200 mb-3">{t('settings.integrations.slack.features')}</h4>
                        <ul className="space-y-2 text-sm text-gray-400">
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.slack.feature1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.slack.feature2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.slack.feature3')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-emerald-500 mt-1">✓</span>
                            <span>{t('settings.integrations.slack.feature4')}</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Future Integrations - Placeholder */}
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="text-gray-400">{t('settings.integrations.otherIntegrations')}</CardTitle>
                <CardDescription>
                  {t('settings.integrations.otherIntegrationsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* GitHub Placeholder */}
                  <div className="rounded-lg border border-gray-700/50 p-4 opacity-50">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-gray-500/20 flex items-center justify-center">
                        <span className="text-xl">🐙</span>
                      </div>
                      <h5 className="font-medium text-gray-300">GitHub</h5>
                    </div>
                    <p className="text-xs text-gray-500">{t('settings.integrations.comingSoon')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Atendimento da organização (spec 157) */}
        <TabsContent value="atendimento">
          <AtendimentoDaOrganizacao />
        </TabsContent>

        {/* Organization Tab - Placeholder */}
        <TabsContent value="organization">
          <Card variant="glass">
            <CardHeader>
              <CardTitle>{t('settings.organization.title')}</CardTitle>
              <CardDescription>
                {t('settings.organization.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg bg-gray-700/20 border border-gray-700/50 p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">
                  {t('settings.organization.placeholder')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Members Tab - Placeholder */}
        <TabsContent value="members">
          <Card variant="glass">
            <CardHeader>
              <CardTitle>{t('settings.members.title')}</CardTitle>
              <CardDescription>
                {t('settings.members.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg bg-gray-700/20 border border-gray-700/50 p-8 text-center">
                <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">
                  {t('settings.members.placeholder')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys">
          <ApiKeysTab />
        </TabsContent>

        {/* LLM Keys Tab */}
        <TabsContent value="llm-keys">
          <LlmKeysTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
